import { useSelector } from 'react-redux';

// Resolve a single master-data label in the currently active language.
// The lookup hits a tiny redux selector so components only re-render when THEIR
// specific (moduleName, entityId, field) combination changes — not on every other
// translation update or language switch that doesn't touch their cell.
//
// Usage:
//   const name = useMasterLabel('masterSpecialty', spec.specialtyId, 'name', spec.name);
//   <h3>{name}</h3>
//
// If the cached locale doesn't have a translation for this cell, the fallback is
// returned instead (usually the English value already baked into the API response).
export const useMasterLabel = (moduleName, entityId, field, fallback = '') => {
    const value = useSelector((state) => {
        const tree = state.translation?.masterLocaleTree;
        if (!tree || !moduleName || !entityId || !field) return undefined;
        return tree?.[moduleName]?.[entityId]?.[field];
    });

    if (value === undefined || value === null || value === '') return fallback;
    return value;
};

// ---------------------------------------------------------------------------
// FOR BUILDING OPTION LISTS.
//
// useMasterLabel is a HOOK, so it cannot be called inside a .map() — the number
// of hook calls would change with the length of the list. That is why translated
// options were rendered through a tiny per-row <option> component, and why those
// selects could not be turned into a searchable one: a combobox needs an ARRAY
// of { value, label }, not a component per row.
//
// So: read the tree ONCE with the hook below, then resolve as many labels as you
// like with the plain function. One hook call, any number of labels.
//
//   const tree = useMasterLocaleTree();
//   const options = specialities.map((s) => ({
//     value: s,
//     label: resolveMasterLabel(tree, 'masterSpecialty', idOf(s), 'name', s),
//   }));
export const useMasterLocaleTree = () =>
    useSelector((state) => state.translation?.masterLocaleTree);

export const resolveMasterLabel = (tree, moduleName, entityId, field, fallback = '') => {
    if (!tree || !moduleName || !entityId || !field) return fallback;
    const value = tree?.[moduleName]?.[entityId]?.[field];
    if (value === undefined || value === null || value === '') return fallback;
    return value;
};

// Bulk variant: resolves every field of a single master entity at once. Prefer this
// when a card renders 3+ fields from the same entity — one selector call instead of three.
export const useMasterEntity = (moduleName, entityId, fallback = {}) => {
    const entity = useSelector((state) => state.translation?.masterLocaleTree?.[moduleName]?.[entityId]);
    if (!entity) return fallback;
    return { ...fallback, ...entity };
};
